'use client'

import { useEffect, useState } from 'react'
import { getAuthHeaders } from '@/lib/api-utils'
import { fetchWithAuthHandler } from '@/lib/api-error-handler'

/**
 * Sync user preferences from Firebase to localStorage for cross-device sync
 * This hook fetches preferences on app load and updates localStorage
 */
export function useSyncPreferences() {
    const [synced, setSynced] = useState(false)

    useEffect(() => {
        // Check if user is authenticated before syncing
        try {
            const authHeaders = getAuthHeaders()
            const hasAuth = authHeaders && Object.keys(authHeaders).length > 0
            if (!hasAuth) {
                // User not authenticated, skip sync
                return
            }
        } catch {
            // Error getting auth headers, user not authenticated
            return
        }

        // Only run sync after a delay to not block initial page load
        const timer = setTimeout(() => {
            syncPreferencesFromFirebase()
        }, 2000) // Wait 2 seconds after page load

        return () => clearTimeout(timer)
    }, [])

    async function syncPreferencesFromFirebase() {
        try {
            // Get current user to fetch their preferences
            const authHeaders = getAuthHeaders()

            // Add timeout to prevent hanging
            const controller = new AbortController()
            const timeoutId = setTimeout(() => controller.abort(), 5000) // 5 second timeout

            let userResponse
            try {
                // Use API proxy instead of direct Redmine URL to avoid CORS and env issues
                userResponse = await fetchWithAuthHandler(`/api/redmine?path=/users/current.json`, {
                    headers: authHeaders,
                    signal: controller.signal
                })
                clearTimeout(timeoutId)
            } catch (err: any) {
                clearTimeout(timeoutId)
                // Silently ignore AbortError (timeout) and other network errors
                if (err.name === 'AbortError') {
                    // Request timed out, skipping sync
                }
                return
            }

            if (!userResponse.ok) {
                // Silently skip - this is expected when not authenticated
                return
            }

            const userData = await userResponse.json()
            const userId = userData.user.id

            // Fetch preferences from Firebase using GET with timeout
            const controller2 = new AbortController()
            const timeoutId2 = setTimeout(() => controller2.abort(), 5000)

            let prefsResponse
            try {
                prefsResponse = await fetchWithAuthHandler(`/api/user-preferences?userId=${userId}`, {
                    method: 'GET',
                    headers: { 'Content-Type': 'application/json' },
                    signal: controller2.signal
                })
                clearTimeout(timeoutId2)
            } catch (err: any) {
                clearTimeout(timeoutId2)
                // Silently ignore AbortError (timeout) and other network errors
                if (err.name === 'AbortError') {
                    // Preferences fetch timed out, skipping sync
                }
                return
            }

            if (!prefsResponse.ok) {
                // Silently skip - no preferences is normal for new users
                return
            }

            const { prefs } = await prefsResponse.json()

            if (!prefs) {
                return
            }

            // Sync theme preferences to localStorage - using same structure as EOD reminders
            // Only sync if there are actual theme preferences saved, otherwise keep defaults
            if (prefs.fontFamily || prefs.fontSize || prefs.colorScheme || prefs.theme) {
                const currentThemePrefs = localStorage.getItem('oxigen-theme-preferences')
                const existingPrefs = currentThemePrefs ? JSON.parse(currentThemePrefs) : {}

                const themePrefs: any = { ...existingPrefs }

                if (prefs.fontFamily) themePrefs.fontFamily = prefs.fontFamily
                if (prefs.fontSize) themePrefs.fontSize = prefs.fontSize
                if (prefs.colorScheme) themePrefs.colorScheme = prefs.colorScheme
                if (prefs.theme) themePrefs.theme = prefs.theme

                localStorage.setItem('oxigen-theme-preferences', JSON.stringify(themePrefs))
                // Theme preferences synced from Firebase

                // Apply theme mode if present
                if (prefs.theme) {
                    const root = document.documentElement
                    if (prefs.theme === 'dark') {
                        root.classList.add('dark')
                    } else if (prefs.theme === 'light') {
                        root.classList.remove('dark')
                    }
                    // Update next-themes storage
                    localStorage.setItem('theme', prefs.theme)
                }

                // Re-apply theme after sync
                const { applyStoredTheme } = await import('@/app/theme-loader')
                applyStoredTheme()
            }

            setSynced(true)
        } catch (error) {
            console.error('[Preferences Sync] Error syncing preferences:', error)
        }
    }

    return { synced }
}
